import type { ReactNode } from 'react';
import { useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';

interface Props {
  children: ReactNode;
}

function readBuildVersionFromScripts(): number | null {
  try {
    const target =
      document.querySelector(
        'script[type="module"][src*="index-"][src*=".js?v="]'
      ) ??
      document.querySelector(
        'script[type="module"][src*="/src/index.tsx"][src*="v="]'
      );

    if (!target) return null;
    const src = target.getAttribute('src');
    if (!src) return null;
    const url = new URL(src, window.location.href);
    const v = url.searchParams.get('v');
    if (!v) return null;
    const timestamp = Number(v);
    return Number.isNaN(timestamp) ? null : timestamp;
  } catch {
    return null;
  }
}

export function BuildVersionProvider({ children }: Props) {
  const [timestamp, setTimestamp] = useState<number | null>(null);

  useEffect(() => {
    setTimestamp(readBuildVersionFromScripts());
  }, []);

  const label = useMemo(() => {
    if (!timestamp) return '';
    return `Updated: ${new Date(timestamp).toLocaleString()}`;
  }, [timestamp]);

  return (
    <>
      {children}
      {label && (
        <Box
          sx={{
            position: 'absolute',
            bottom: 8,
            left: '50%',
            transform: 'translateX(-50%)',
            fontSize: 12,
            color: '#cacbd6',
            px: 1,
            py: 0.5,
            borderRadius: 1,
            zIndex: 1,
            pointerEvents: 'none',
          }}
        >
          {label}
        </Box>
      )}
    </>
  );
}

export default BuildVersionProvider;
